import { MDBBtn, MDBChip, MDBCol, MDBContainer, MDBLightbox, MDBLightboxItem, MDBRow, MDBTypography } from 'mdb-react-ui-kit';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

// Custom hook for truncation and toggling logic
export const useTruncateToggle = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        const maxHeight = 60;
        setIsTruncated(textRef.current.scrollHeight > maxHeight);
      }
    };

    checkTruncation();

    window.addEventListener('resize', checkTruncation);

    return () => window.removeEventListener('resize', checkTruncation);
  }, []);

  const toggleExpanded = () => {
    setIsExpanded((prev) => !prev);
  };

  return {
    isExpanded,
    isTruncated,
    toggleExpanded,
    textRef,
  };
};

const sortParagraphs = (paragraphs) => {
  if (!Array.isArray(paragraphs)) return paragraphs;

  return paragraphs
    .slice() // Make a shallow copy to avoid mutating the original array
    .sort((a, b) => a.orderNumber - b.orderNumber) // Sort by orderNumber
    .map((paragraph) => ({
      ...paragraph,
      // Recursively sort nested paragraphs
      paragraphs: sortParagraphs(paragraph.paragraphs || []),
    }));
};

const ParagraphWithToggle = ({ paragraph, level = 0 }) => {
  const { isExpanded, isTruncated, toggleExpanded, textRef } = useTruncateToggle();
  const paddingLeft = `${level * 1.5}rem`; // Adjust `1.5rem` for desired indentation per level


  return (
    <div className="paragraph-with-toggle align-items-center justify-content-center" style={{ paddingLeft }}>
      <p className=" mt-0 mb-0 text-start text-black ms-2">
        {paragraph.type === 'link' ? (
          <a href={paragraph.textValue} target="_blank" rel="noopener noreferrer">
            <MDBBtn
              type="button"
              color="link"
              rippleColor="dark"
              className={`${paragraph.heading?.textColor || 'text-primary'} fw-bold mb-1 text-capitalize ${paragraph.heading.fontSize}`}
            >
              {paragraph.textValue}
            </MDBBtn>
          </a>
        ) : paragraph.type === 'caption' ? (
          <MDBChip
            size="md"
            className="d-flex align-items-center justify-content-start w-100"
            style={{
              minHeight: '40px',
              padding: '10px',
              wordBreak: 'break-word',
              whiteSpace: 'normal',
              overflowWrap: 'break-word',
            }}
          >
            {/* Render image inside the chip */}
            {paragraph.heading?.image && (
              <img
                style={{
                  width: '40px',
                  height: '40px',
                  marginRight: '10px',
                  objectFit: 'cover',
                  borderRadius: '50%',
                }}
                src={paragraph.heading?.image}
                alt="caption"
              />
            )}
            <div className="chip-content">
              {paragraph.heading?.textValue && (
                <MDBTypography
                  tag={`${paragraph.heading?.textSize || 'h4'}`}
                  className={`${paragraph.heading?.textColor || 'text-primary'} fw-bold mb-1 text-capitalize ${paragraph.heading.fontSize}`}
                >
                  {paragraph.heading.textValue}
                </MDBTypography>
              )}
              {paragraph.textValue && (
                <figcaption className="blockquote-footer mb-0 ">
                  <cite title={paragraph.textValue} className={`${paragraph?.textColor || 'text-success'} fw-bold `}>
                    {paragraph.textValue}
                  </cite>
                </figcaption>
              )}
            </div>
          </MDBChip>
        ) : (
          <>
            {paragraph.heading?.textValue ?
            <>  <MDBTypography
                tag={`${paragraph.heading?.textSize || 'h2'}`}
                variant={`${paragraph.heading?.textSize || 'h2'}`}
                className={`${paragraph.heading?.textColor || 'text-primary'} fw-bold mb-1 text-capitalize ${paragraph.heading.textSize ==='h2' || paragraph.heading.textSize ==='h1' ? 'text-center' : 'text-start'} `}
              >
                {paragraph.heading.textValue}
              </MDBTypography>


{paragraph.textValue && (
  <p ref={textRef} className={`text-${paragraph?.textColor || 'dark'} ms-2`}>
    {isTruncated && !isExpanded ? `${paragraph.textValue.slice(0, 100)}...` : paragraph.textValue}
  </p>
)}
</>: <p ref={textRef} className={`text-${paragraph?.textColor || 'dark'} ms-2`}>
                {isTruncated && !isExpanded ? `${paragraph.textValue.slice(0, 100)}...` : paragraph.textValue}
 
              </p>
            }
                         { isTruncated && 
              <MDBBtn type="button" size="sm" color="link" className="text-lowercase" onClick={toggleExpanded}>
                {isExpanded ? 'Show Less' : 'Show More'}
              </MDBBtn>
}
            
          </>
        )}
      </p>
      {paragraph.paragraphs && paragraph.paragraphs.length > 0 && (
        <div className="nested-paragraphs">
          {paragraph.paragraphs.map((subParagraph, index) => (
            <ParagraphWithToggle key={index} paragraph={subParagraph} level={level + 1} />
          ))}
        </div>
      )}
    </div>
  );
};



const RenderParagraphs = ({ paragraphs }) => (
  <div className="paragraphs-container">
    {paragraphs.map((paragraph, index) => (
      <ParagraphWithToggle key={index} paragraph={paragraph} />
    ))}
  </div>
);

const RenderDescriptions = ({ descriptions }) => {
  return (
    <MDBContainer className="description-container text-start">
      {descriptions.map((description, index) => {
        const hasImage = !!description.heading?.image;

        return (
          <MDBRow key={index} className={`description-block ${index % 2 === 0 ? '' : 'flex-row-reverse flex-row bg-light'} rounded`}>
          { description.heading.type==='caption' ?  <MDBChip className='text-center ' size="lg" style={{ minHeight: '40px', height: 'auto', padding: '10px', display: 'inline-block' }}>
                {description.heading?.textValue && (
                  <MDBTypography
                    tag={`${description.heading?.textSize || 'h4'}`}
                    variant={description.heading?.textSize || 'h5'}
                    className={`${description.heading?.textColor || 'text-dark'} fw-bold mb-1 text-capitalize`}
                  >
                    {description.heading.textValue}
                  </MDBTypography>
                )}
                <figcaption className="blockquote-footer mb-0">
                  <cite className={`${description?.textColor || 'text-success'} fw-bold mb-1`} title={description.textValue}>
                    {description.textValue}
                  </cite>
                </figcaption>
              </MDBChip> :
              <>
              {description.heading?.textValue && (
                <MDBTypography
                  tag={`${description.heading?.textSize || 'h1'}`}
                  variant={description.heading?.textSize || 'h1'}
                  className={`${description.heading?.textColor || 'text-success'} text-start mb-0 text-capitalize align-items-center justify-content-center mt-3`}
                  style={{ color: description.heading?.textColor }}
                >
                  {description.heading.textValue}
                </MDBTypography>
              )}
              {description.textValue && (
                <span className={`text-${description?.textColor ? description.textColor : 'dark'} `}>
                  {description.textValue}
                </span>
              )}
            
            </>}

            <MDBCol md="12" className={ `${index===0 ? 'text-center' : ''} content-wrapper  justify-content-center align-items-center`}>
              
              {hasImage &&
             
                <MDBLightbox>
                <MDBLightboxItem  style={{
                  maxWidth: '300px',
               
                  objectFit: 'cover',
                  float: index % 2 === 0 ? 'left' : 'right',
                  margin: index % 2 === 0 ? '0 15px 10px 0' : '0 0 10px 15px',
                }}
                src={description.heading.image}
                fullscreenSrc={description.heading.image}
                className='w-100 shadow-1-strong rounded'
              />
</MDBLightbox>  
             }
                      <RenderParagraphs paragraphs={description.paragraphs} />
              
            </MDBCol>
          </MDBRow>
        );
      })}
    </MDBContainer>
  );
};


RenderParagraphs.propTypes = {
  paragraphs: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string, // Validate `type` in paragraphs
      textValue: PropTypes.string,
      textColor: PropTypes.string,
      orderNumber: PropTypes.number,
      fontSize: PropTypes.string,
      heading: PropTypes.shape({
        textValue: PropTypes.string,
        fontSize: PropTypes.string,
        textSize: PropTypes.string,
        textColor: PropTypes.string,
        image: PropTypes.string, // Validate `image` in heading
      }),
      paragraphs: PropTypes.arrayOf(PropTypes.object), // Validate nested paragraphs
    })
  ).isRequired,
  level: PropTypes.number, // Validate `level` as a number
};

ParagraphWithToggle.propTypes = {
  
  paragraph: PropTypes.shape({
    type: PropTypes.string, // Validate `type` in paragraph
    textValue: PropTypes.string,
    textColor: PropTypes.string,
    fontSize: PropTypes.string,
    heading: PropTypes.shape({
      textValue: PropTypes.string,
      textSize: PropTypes.string,
      textColor: PropTypes.string,
      fontSize: PropTypes.string,
      image: PropTypes.string, // Validate `image` in heading
    }),
    paragraphs: PropTypes.arrayOf(PropTypes.shape({
      textValue: PropTypes.string,
      type: PropTypes.string,
    })), // Validate nested paragraphs
  }).isRequired,
  level: PropTypes.number,
};

RenderDescriptions.propTypes = {
  descriptions: PropTypes.arrayOf(
    PropTypes.shape({
      heading: PropTypes.shape({
        textValue: PropTypes.string,
        textSize: PropTypes.string,
        textColor: PropTypes.string,
        fontSize: PropTypes.string,
        image: PropTypes.string,
      }),
      textValue: PropTypes.string,
      textColor: PropTypes.string,
      paragraphs: PropTypes.arrayOf(PropTypes.object),
      fontSize: PropTypes.string,
    })
  ).isRequired,
};
export default RenderDescriptions;
